import React, { useState, useEffect } from 'react';
import { usePartnerBrand } from '@/lib/partnerBrand';
import type { PartnerBrand } from '@/lib/partners';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  Globe,
  Sparkles,
  RotateCw,
  FileArchive,
  Scissors,
  FileText,
  Layers,
  Link2,
  ChevronDown,
  Wrench,
  LogOut,
  ExternalLink,
  Settings,
  Video,
  Users,
  Crown,
  Coins,
  BellRing,
  Mail,
  Menu,
  Plus,
  Home,
  Store,
  ShoppingBag,
  Package,
  Receipt,
  BookOpen,
  AudioLines,
  Image,
  Type,
  Captions,
  CalendarDays,
  MessageCircle,
  Wallet,
  Sun,
  Moon,
  Wand2,
  Compass,
} from 'lucide-react';
import { useTheme } from 'next-themes';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { openCommunity } from '@/config/community';
import { useAuthContext } from '@/contexts/AuthContext';
import { useCredits } from '@/hooks/useCredits';
import { fetchReaderAttention, type ReaderAttention } from '@/services/readerApi';
import { fetchSiteAttention, type SiteAttention } from '@/services/siteApi';
import { CharterBadge } from '@/components/CharterBadge';
import { useSubscription } from '@/hooks/useSubscription';
import { getAuthHeaders } from '@/hooks/useAuth';
import { LoginModal } from './LoginModal';
import { CreditPurchaseModal } from './credits/CreditPurchaseModal';

// Tools shown in the Tools dropdown — order matters (enhancers up top because
// they're the marquee features; utility tools follow).
const ENHANCER_TOOLS = [
  { path: '/audition', label: 'Audition Enhancer', icon: Video, description: 'Polish your audition', usesCredits: true },
  // Video Enhancer hidden from the menu (AI-render output not up to par yet); route still lives at /video-enhancer.
  // { path: '/video-enhancer', label: 'Video Enhancer', icon: Sparkles, description: 'Smart editing tools', usesCredits: true },
  { path: '/audio-enhancer', label: 'Audio Enhancer', icon: AudioLines, description: 'Studio-grade voice cleanup', usesCredits: true },
  { path: '/background-remover', label: 'Background Remover', icon: Image, description: 'Replace, green-screen, AI backdrops', usesCredits: true },
  { path: '/name-slate', label: 'Name Slate', icon: Type, description: 'Name, role, agency', usesCredits: true },
  { path: '/captions', label: 'Captions', icon: Captions, description: 'Burn captions into your tape', usesCredits: true },
];
const FREE_TOOLS = [
  { path: '/video-splitter', label: 'Split / Trim', icon: Scissors, description: 'Divide into clips' },
  { path: '/video-stitcher', label: 'Stitch', icon: Layers, description: 'Combine videos' },
  { path: '/video-concatenate', label: 'Merge', icon: Link2, description: 'Join clips end-to-end' },
  { path: '/video-compressor', label: 'Compress', icon: FileArchive, description: 'Reduce file size' },
  { path: '/video-transcriber', label: 'Transcribe', icon: FileText, description: 'Generate captions' },
  { path: '/video-rotator', label: 'Rotate', icon: RotateCw, description: 'Fix orientation' },
];
const ALL_TOOLS = [...ENHANCER_TOOLS, ...FREE_TOOLS];

// Scene Edit (Runway) is admin-only while it's being tested; the endpoint enforces it too.
const SCENE_EDIT_TOOL = { path: '/scene-edit', label: 'Scene Edit', icon: Wand2, description: 'Put your scene in a new world', usesCredits: true };

// Initials avatar pulled into the header — small, gradient, display-italic.
// Matches the chrome.jsx TopNav avatar. Used at 36px on desktop, 32px on sm.
const ProfileAvatar: React.FC<{ name: string | null; size?: number }> = ({ name, size = 36 }) => {
  const initial = (name || 'J').trim().charAt(0).toUpperCase() || 'J';
  return (
    <div
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, var(--hj-blue) 0%, oklch(0.5 0.18 320) 100%)',
        color: 'oklch(0.16 0.005 70)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: Math.round(size * 0.36),
        fontFamily: 'var(--hj-font-display)',
        fontStyle: 'italic',
        flexShrink: 0,
      }}
    >
      {initial}
    </div>
  );
};

// "x Limelite" beside the wordmark: the partner's own logo, ink on light, white on dark.
const PartnerMark: React.FC<{ brand: PartnerBrand; height: number }> = ({ brand, height }) => (
  <span className="inline-flex items-center" style={{ gap: 8 }}>
    <span aria-hidden style={{ fontSize: Math.round(height * 0.8), color: 'var(--hj-ink-3)', lineHeight: 1 }}>×</span>
    <img src={brand.logo.replace('.svg', '-ink.svg')} alt={brand.name} className="dark:hidden" style={{ height, width: 'auto', display: 'block' }} />
    <img src={brand.logo} alt={brand.name} className="hidden dark:block" style={{ height, width: 'auto' }} />
  </span>
);

export const AppHeader: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuthContext();
  // People who came through a partner (Limelite) see "Hey Joe x <partner>".
  const partnerBrand = usePartnerBrand(isAuthenticated, user?.id);
  const { balance, isAuthenticated: creditsAuth } = useCredits();
  const { canSellProducts } = useSubscription();
  const { theme, setTheme, resolvedTheme } = useTheme();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [isReader, setIsReader] = useState(false);
  // Show a dot on "Manage Account" when the member has an unredeemed Ticket Rewards code.
  const [hasReward, setHasReward] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) { setHasReward(false); return; }
    fetch('/api/ticket-rewards', { headers: getAuthHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setHasReward(!!d?.rewards?.some((x: { status: string; code: string | null }) => x.status === 'granted' && x.code)))
      .catch(() => setHasReward(false));
  }, [isAuthenticated]);

  const rewardDot = hasReward
    ? <span aria-label="Reward available" style={{ width: 7, height: 7, borderRadius: 999, background: 'var(--hj-blue)', display: 'inline-block', marginLeft: 6, flexShrink: 0 }} />
    : null;

  // Does this user have a reader profile? Drives the reader group in the account
  // menu.
  //
  // This used to read creator_pages.readerId, a castingstation-era field that is
  // absent on every creator page we have — so the "My Reader Profile" item never
  // rendered for anybody, and it pointed at the PUBLIC profile view rather than
  // the editor. Readers had no way into their own settings short of typing the
  // URL. Plus's readers collection is the authority now, same as everywhere else.
  //
  // Gated on the fetch because most users aren't readers, but a failed request
  // leaves the last known answer alone rather than hiding the menu — that's the
  // "My Store falls out of the menu" bug noted below.
  useEffect(() => {
    if (!isAuthenticated) {
      setIsReader(false);
      return;
    }
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    if (!token) return;
    fetch('/api/reader/profile', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(data => { if (data && data.profile) setIsReader(true); })
      .catch(() => { /* keep whatever we already knew */ });
  }, [isAuthenticated]);

  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    if (!isAuthenticated) { setIsAdmin(false); return; }
    fetch('/api/admin/check', { headers: getAuthHeaders() })
      .then(r => (r.ok ? r.json() : null))
      .then(data => setIsAdmin(!!data?.isAdmin))
      .catch(() => setIsAdmin(false));
  }, [isAuthenticated]);
  const enhancerTools = [...ENHANCER_TOOLS, SCENE_EDIT_TOOL];
  const allTools = [...enhancerTools, ...FREE_TOOLS];

  // Reader-side attention: an ask by name, a request to take, readers waiting
  // on a request of yours, unread messages. Polled so the header can say so on
  // every page instead of making people go look in a tab.
  const [attention, setAttention] = useState<ReaderAttention | null>(null);
  useEffect(() => {
    if (!isAuthenticated) { setAttention(null); return; }
    let stop = false;
    const tick = () => fetchReaderAttention().then(a => { if (!stop) setAttention(a); });
    tick();
    const itv = setInterval(tick, 60000);
    const onFocus = () => tick();
    window.addEventListener('focus', onFocus);
    return () => { stop = true; clearInterval(itv); window.removeEventListener('focus', onFocus); };
  }, [isAuthenticated, location.pathname]);
  const attentionLabel = (a: ReaderAttention) =>
    a.askedByName ? `${a.askedByName === 1 ? 'Someone asked for you' : `${a.askedByName} asked for you`}`
    : a.readersWaiting ? `${a.readersWaiting === 1 ? 'A reader is waiting' : `${a.readersWaiting} readers are waiting`}`
    : a.openRequests ? `${a.openRequests} open ${a.openRequests === 1 ? 'request' : 'requests'}`
    : `${a.unreadMessages} unread`;
  // Site hosting: unread contact-form messages, and notices that bounced. Never by
  // email; the header is how an actor finds out (docs/SCOPE_site_hosting.md §9).
  const [siteAttention, setSiteAttention] = useState<SiteAttention | null>(null);
  useEffect(() => {
    if (!isAuthenticated) { setSiteAttention(null); return; }
    let stop = false;
    const tick = () => fetchSiteAttention().then(a => { if (!stop) setSiteAttention(a); });
    tick();
    const itv = setInterval(tick, 120000);
    return () => { stop = true; clearInterval(itv); };
  }, [isAuthenticated, location.pathname]);
  const attentionTab = (a: ReaderAttention) => (a.askedByName ? 'reading' : a.readersWaiting ? 'sent' : a.openRequests ? 'take' : a.unreadMessages ? 'reading' : 'sent');

  const currentTool = allTools.find((t) => location.pathname === t.path);
  const userName =
    user?.firstName ||
    user?.first_name ||
    (user?.name ? user.name.split(' ')[0] : null) ||
    (user?.email ? user.email.split('@')[0] : null);
  const isSubscriber = balance?.tier && balance.tier !== 'free';
  const creditCount = balance?.totalCredits ?? 0;
  const creditDisplay = creditCount.toLocaleString();

  const openCasting = () => window.open('https://casting.heyjoe.io', '_blank', 'noopener,noreferrer');
  const goTo = (path: string) => {
    setMobileNavOpen(false);
    navigate(path);
  };

  const navLinks: { label: string; path?: string; external?: () => void; icon: React.ElementType; isActive: () => boolean }[] = [
    { label: 'Home', path: '/', icon: Home, isActive: () => location.pathname === '/' },
    // Tools is a dropdown — see desktop nav rendering below; mobile sheet just lists each tool.
    { label: 'Readers', path: '/readers', icon: Users, isActive: () => location.pathname === '/readers' },
    { label: 'Hey Joe Casting', external: openCasting, icon: Video, isActive: () => false },
  ];

  return (
    <>
      <header
        className="sticky top-0 z-40"
        style={{
          background: 'var(--hj-bg)',
          borderBottom: '1px solid var(--hj-hairline-soft)',
        }}
      >
        {/* ───── Desktop / md+ header — ≥ 768px ──────────────────────────── */}
        <div
          className="hidden md:flex items-center mx-auto"
          style={{ maxWidth: 1440, padding: '0 32px', height: 68, gap: 24 }}
        >
          {/* Wordmark lockup — the brand PNG is dark line-art on transparent,
              so we apply the invert+sepia filter (matching chrome.jsx
              JoeWordmark) to render it as cream on the dark header. The
              natural angle of the handwritten wordmark is baked into the PNG. */}
          <Link to="/" className="flex items-center gap-3 no-underline">
            <img
              src="/brand/wordmark-heyjoe.png"
              alt="Hey Joe"
              className="dark:[filter:invert(96%)_sepia(8%)_saturate(280%)_hue-rotate(345deg)_brightness(102%)]"
              style={{
                height: 36,
                width: 'auto',
                display: 'block',
              }}
            />
            {partnerBrand && <PartnerMark brand={partnerBrand} height={22} />}
            <span
              className="hidden lg:block"
              style={{ width: 1, height: 24, background: 'var(--hj-hairline)' }}
            />
            <div className="hidden lg:flex items-center gap-2">
              <span
                className="font-hj-display"
                style={{ fontSize: 22, color: 'var(--hj-ink)' }}
              >
                Plus
              </span>
              {isSubscriber && (
                <span
                  className="inline-flex items-center gap-1 mono-label"
                  style={{
                    padding: '2px 8px',
                    borderRadius: 999,
                    border: '1px solid var(--hj-blue)',
                    color: 'var(--hj-yellow-ink)',
                    background: 'var(--hj-blue-tint)',
                    fontSize: 10,
                  }}
                >
                  <Crown size={10} strokeWidth={2} />
                  Member
                </span>
              )}
            </div>
          </Link>

          {/* Nav */}
          <nav className="flex items-center" style={{ gap: 2, marginLeft: 24 }}>
            <NavButton
              label="Home"
              active={location.pathname === '/'}
              onClick={() => navigate('/')}
            />
            {/* Tools dropdown — caret triggers menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 transition-colors"
                  style={{
                    padding: '8px 12px',
                    borderRadius: 'var(--hj-r-sm)',
                    background: 'transparent',
                    border: 0,
                    color: currentTool ? 'var(--hj-ink)' : 'var(--hj-ink-2)',
                    fontSize: 14,
                    fontWeight: 500,
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                  }}
                >
                  {currentTool ? (
                    <>
                      <currentTool.icon size={14} strokeWidth={1.8} />
                      <span>{currentTool.label}</span>
                    </>
                  ) : (
                    <span>Tools</span>
                  )}
                  <ChevronDown size={12} strokeWidth={2} className="opacity-70" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64 max-h-[75vh] overflow-y-auto">
                <DropdownMenuLabel className="flex items-center gap-1.5 text-xs text-primary">
                  <Sparkles className="w-3 h-3" />
                  Video Enhancer
                  <span className="ml-auto text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded">Uses video tokens</span>
                </DropdownMenuLabel>
                {enhancerTools.map(({ path, label, icon: Icon, description }) => (
                  <DropdownMenuItem
                    key={path}
                    onClick={() => navigate(path)}
                    className={`${location.pathname === path ? 'bg-muted' : ''} items-start ml-2`}
                  >
                    <Icon className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                    <div className="flex flex-col text-left">
                      <span className="font-medium">{label}</span>
                      <span className="text-xs text-muted-foreground">{description}</span>
                    </div>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Wrench className="w-3 h-3" />
                  Video Tools
                </DropdownMenuLabel>
                {FREE_TOOLS.map(({ path, label, icon: Icon, description }) => (
                  <DropdownMenuItem
                    key={path}
                    onClick={() => navigate(path)}
                    className={`${location.pathname === path ? 'bg-muted' : ''} items-start ml-2`}
                  >
                    <Icon className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                    <div className="flex flex-col text-left">
                      <span className="font-medium">{label}</span>
                      <span className="text-xs text-muted-foreground">{description}</span>
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {/* My Site — plain link between Tools and Marketplace (Joe, Oct 19 2026). Sites
                was reachable only from the account menu, so nobody found it. */}
            <button
              type="button"
              onClick={() => navigate('/my-site')}
              className="inline-flex items-center gap-1.5 transition-colors"
              style={{
                padding: '8px 12px',
                borderRadius: 'var(--hj-r-sm)',
                background: 'transparent',
                border: 0,
                color: location.pathname === '/my-site' ? 'var(--hj-ink)' : 'var(--hj-ink-2)',
                fontSize: 14,
                fontWeight: 500,
                fontFamily: 'inherit',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              <span>My Site</span>
            </button>
            {/* Marketplace dropdown — browse + sell */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 transition-colors"
                  style={{
                    padding: '8px 12px',
                    borderRadius: 'var(--hj-r-sm)',
                    background: 'transparent',
                    border: 0,
                    color: (location.pathname === '/marketplace' || location.pathname === '/discover' || location.pathname === '/my-store') ? 'var(--hj-ink)' : 'var(--hj-ink-2)',
                    fontSize: 14,
                    fontWeight: 500,
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                  }}
                >
                  <span>Marketplace</span>
                  <ChevronDown size={12} strokeWidth={2} className="opacity-70" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-60">
                <DropdownMenuItem
                  onClick={() => navigate('/discover')}
                  className={`${location.pathname === '/discover' ? 'bg-muted' : ''} items-start ml-2`}
                >
                  <Compass className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <div className="flex flex-col text-left">
                    <span className="font-medium">Discover</span>
                    <span className="text-xs text-muted-foreground">What's live, what's next, and class clips</span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/marketplace')}
                  className={`${location.pathname === '/marketplace' ? 'bg-muted' : ''} items-start ml-2`}
                >
                  <ShoppingBag className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <div className="flex flex-col text-left">
                    <span className="font-medium">Browse marketplace</span>
                    <span className="text-xs text-muted-foreground">Courses & content from creators</span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/calendar')}
                  className={`${location.pathname === '/calendar' ? 'bg-muted' : ''} items-start ml-2`}
                >
                  <CalendarDays className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <div className="flex flex-col text-left">
                    <span className="font-medium">Featured event &amp; class calendar</span>
                    <span className="text-xs text-muted-foreground">Upcoming live sessions &amp; classes</span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/my-store')}
                  className={`${location.pathname === '/my-store' ? 'bg-muted' : ''} items-start ml-2`}
                >
                  <Store className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <div className="flex flex-col text-left">
                    <span className="font-medium">My store</span>
                    <span className="text-xs text-muted-foreground">Sell your courses — free</span>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <NavButton
              label="Readers"
              active={location.pathname === '/readers'}
              onClick={() => navigate('/readers')}
            />
            {/* Community moved into the account menu (Joe, Oct 19 2026): the top bar was
                crowding and wrapping once My Site joined it. */}
            <NavButton label="Hey Joe Casting" external onClick={openCasting} />
          </nav>

          {/* Right rail */}
          <div className="flex items-center ml-auto" style={{ gap: 14 }}>
            <button
              type="button"
              onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
              title={resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              aria-label={resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              className="inline-flex items-center justify-center rounded-full transition-colors"
              style={{ width: 36, height: 36, border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-3)', background: 'transparent' }}
            >
              {resolvedTheme === 'dark' ? <Sun size={17} strokeWidth={1.8} /> : <Moon size={17} strokeWidth={1.8} />}
            </button>
            {/* Charter badge: a real badge, permanent. Sits between the casting link
                and the credit count, and never depends on the current subscription. */}
            {isAuthenticated && attention && attention.total > 0 && (
              <button
                type="button"
                onClick={() => navigate(`/readers/requests?tab=${attentionTab(attention)}`)}
                title={attentionLabel(attention)}
                className="inline-flex items-center"
                style={{ gap: 7, padding: '7px 12px', borderRadius: 999, border: '1px solid var(--hj-yellow)', background: 'var(--hj-yellow)', color: '#0A0A0A', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                <BellRing size={14} strokeWidth={2.2} />
                <span className="hidden md:inline">{attentionLabel(attention)}</span>
                <span className="md:hidden">{attention.total}</span>
              </button>
            )}
            {isAuthenticated && siteAttention && (siteAttention.unread > 0 || siteAttention.undeliverable) && location.pathname !== '/my-site' && (
              <button
                type="button"
                onClick={() => navigate('/my-site')}
                title={siteAttention.undeliverable ? "We couldn't reach your email" : 'New messages from your website'}
                className="inline-flex items-center"
                style={{ gap: 7, padding: '7px 12px', borderRadius: 999, border: '1px solid var(--hj-yellow)', background: 'var(--hj-yellow)', color: '#0A0A0A', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                <Mail size={14} strokeWidth={2.2} />
                <span className="hidden md:inline">
                  {siteAttention.unread > 0 ? `${siteAttention.unread} new ${siteAttention.unread === 1 ? 'message' : 'messages'}` : ''}
                  {siteAttention.undeliverable ? `${siteAttention.unread > 0 ? ' · ' : ''}we couldn't reach your email` : ''}
                </span>
                <span className="md:hidden">{siteAttention.unread || '!'}</span>
              </button>
            )}
            {isAuthenticated && balance?.charterSince && <CharterBadge size={36} since={balance.charterSince} />}
            {/* Hey Joe credit: shown while there is any, so a sale buyer's $50 is in
                view on every page until it's spent. Opens billing, where the
                expiry and what it's good for are spelled out. */}
            {isAuthenticated && (balance?.walletCents ?? 0) > 0 && (
              <button
                type="button"
                onClick={() => navigate('/my-subscriptions')}
                title="Hey Joe credit. Use it at checkout on events, classes and store items."
                aria-label={`$${((balance?.walletCents ?? 0) / 100).toFixed(2)} in Hey Joe credit`}
                className="inline-flex items-center transition-colors"
                style={{ gap: 7, padding: '8px 14px', borderRadius: 999, border: '1px solid var(--hj-hairline)', background: 'transparent', color: 'var(--hj-ink)', fontFamily: 'inherit', cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                <Wallet size={14} strokeWidth={1.8} style={{ color: 'var(--hj-yellow-ink)' }} />
                <span className="tabular" style={{ fontSize: 13, fontWeight: 600 }}>
                  ${((balance?.walletCents ?? 0) / 100).toFixed(2).replace(/\.00$/, '')}
                </span>
                <span className="mono-label hidden md:inline" style={{ fontSize: 10 }}>credit</span>
              </button>
            )}
            {isAuthenticated && creditsAuth() && (
              <button
                type="button"
                onClick={() => setShowPurchaseModal(true)}
                className="inline-flex items-center transition-colors"
                style={{
                  gap: 8,
                  padding: '8px 14px',
                  borderRadius: 999,
                  border: '1px solid var(--hj-hairline)',
                  background: 'transparent',
                  color: 'var(--hj-ink)',
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                }}
                aria-label={`${creditDisplay} video tokens — click to buy more`}
              >
                <Coins size={14} strokeWidth={1.8} style={{ color: 'var(--hj-yellow-ink)' }} />
                <span className="tabular" style={{ fontSize: 13, fontWeight: 600 }}>
                  {creditDisplay}
                </span>
                <span className="mono-label" style={{ fontSize: 10 }}>
                  tokens
                </span>
              </button>
            )}
            {/* "+ Buy video tokens" pill — desktop-only (lg+). At md it moves into
                the avatar dropdown per designer's guidance. */}
            {isAuthenticated && (
              <button
                type="button"
                onClick={() => setShowPurchaseModal(true)}
                className="hidden lg:inline-flex items-center transition-colors"
                style={{
                  gap: 6,
                  padding: '8px 14px',
                  borderRadius: 999,
                  border: '1px solid var(--hj-hairline)',
                  background: 'transparent',
                  color: 'var(--hj-ink-2)',
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                }}
              >
                <Plus size={12} strokeWidth={2} />
                <span className="mono-label" style={{ color: 'inherit', fontSize: 10 }}>
                  Buy video tokens
                </span>
              </button>
            )}
            <span
              className="hidden lg:block"
              style={{ width: 1, height: 24, background: 'var(--hj-hairline)' }}
            />
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center cursor-pointer"
                    style={{ gap: 10, background: 'transparent', border: 0, padding: 0 }}
                    aria-label="Account menu"
                  >
                    <ProfileAvatar name={userName} size={36} />
                    <ChevronDown size={12} strokeWidth={2} style={{ color: 'var(--hj-ink-3)' }} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {userName && (
                    <DropdownMenuLabel className="font-normal">
                      <div className="text-xs text-muted-foreground">Signed in as</div>
                      <div className="text-sm font-medium">{userName}</div>
                    </DropdownMenuLabel>
                  )}
                  <DropdownMenuSeparator />
                  {/* Buy video tokens — surfaced here at md (the standalone pill is
                      lg-only). Always show; tapping at lg+ does the same thing. */}
                  <DropdownMenuItem onClick={() => setShowPurchaseModal(true)} className="lg:hidden">
                    <Plus className="w-4 h-4 mr-2" />
                    Buy video tokens
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/account')}>
                    <Settings className="w-4 h-4 mr-2" />
                    Manage Account
                    {rewardDot}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={openCasting}>
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Hey Joe Casting
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/marketplace')}>
                    <ShoppingBag className="w-4 h-4 mr-2" />
                    Marketplace
                  </DropdownMenuItem>
                  {/* Never gate nav on the subscription fetch — a slow or failed
                      /api/subscription made these silently vanish (the "My Store
                      falls out of the menu" bug). The pages themselves handle
                      non-creators with the free activation flow. */}
                  <DropdownMenuItem onClick={() => navigate('/my-store')}>
                    <Store className="w-4 h-4 mr-2" />
                    My Store
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/my-library')}>
                    <Video className="w-4 h-4 mr-2" />
                    My Library
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/my-purchases')}>
                    <Package className="w-4 h-4 mr-2" />
                    My Purchases
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/my-subscriptions')}>
                    <Receipt className="w-4 h-4 mr-2" />
                    My Subscriptions
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/my-site')}>
                    <Globe className="w-4 h-4 mr-2" />
                    My Site
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={openCommunity}>
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Community
                  </DropdownMenuItem>
                  {/* Everyone who has asked for a reader needs a way back to that
                      request; this used to be reachable only by readers. */}
                  <DropdownMenuItem onClick={() => navigate('/readers/requests')}>
                    <CalendarDays className="w-4 h-4 mr-2" />
                    Reader sessions
                  </DropdownMenuItem>
                  {isReader && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/readers/profile')}>
                        <BookOpen className="w-4 h-4 mr-2" />
                        My Reader Profile
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/reader/payouts')}>
                        <Wallet className="w-4 h-4 mr-2" />
                        Reader Payouts
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-destructive focus:text-destructive">
                    <LogOut className="w-4 h-4 mr-2" />
                    Log Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <button
                type="button"
                onClick={() => setShowLoginModal(true)}
                className="inline-flex items-center"
                style={{
                  padding: '8px 16px',
                  borderRadius: 999,
                  border: '1px solid var(--hj-hairline)',
                  background: 'transparent',
                  color: 'var(--hj-ink)',
                  fontSize: 13,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                }}
              >
                Sign in
              </button>
            )}
          </div>
        </div>

        {/* ───── sm header — < 768px ─────────────────────────────────────── */}
        <div
          className="md:hidden flex items-center justify-between"
          style={{ padding: '0 16px', height: 56 }}
        >
          {/* Hamburger left, in a 44×44 hit target */}
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                aria-label="Open menu"
                className="inline-flex items-center justify-center"
                style={{
                  width: 44,
                  height: 44,
                  margin: '0 -10px 0 -10px',
                  background: 'transparent',
                  border: 0,
                  color: 'var(--hj-ink)',
                  cursor: 'pointer',
                }}
              >
                <Menu size={22} strokeWidth={1.8} />
              </button>
            </SheetTrigger>
            {/* Side drawer, full height. It used to drop from the top capped at 70vh
                with no scroll container, so Tools ran off the bottom of the phone and
                nothing below it could be reached. Now: navigation scrolls in the
                middle, account + credits pin to the bottom where the thumb is. */}
            <SheetContent
              side="right"
              className="p-0 flex flex-col"
              style={{
                background: 'var(--hj-surface-1)',
                borderColor: 'var(--hj-hairline)',
                color: 'var(--hj-ink)',
                width: 'min(88vw, 360px)',
                maxWidth: 'min(88vw, 360px)',
                height: '100dvh',
              }}
            >
              <div
                className="flex-1 overflow-y-auto"
                style={{ padding: '20px 16px 8px', minHeight: 0, WebkitOverflowScrolling: 'touch' }}
              >
                {/* Nav links */}
                <nav className="flex flex-col" style={{ gap: 4 }}>
                  {navLinks.map((link) => {
                    const Icon = link.icon;
                    const active = link.isActive();
                    return (
                      <button
                        key={link.label}
                        type="button"
                        onClick={() => {
                          if (link.external) {
                            link.external();
                            setMobileNavOpen(false);
                          } else if (link.path) {
                            goTo(link.path);
                          }
                        }}
                        className="flex items-center w-full text-left transition-colors"
                        style={{
                          gap: 14,
                          padding: '14px 12px',
                          borderRadius: 'var(--hj-r-sm)',
                          background: active ? 'var(--hj-surface-2)' : 'transparent',
                          border: 0,
                          color: active ? 'var(--hj-ink)' : 'var(--hj-ink-2)',
                          fontSize: 15,
                          fontWeight: active ? 600 : 500,
                          fontFamily: 'inherit',
                          cursor: 'pointer',
                          minHeight: 44,
                        }}
                      >
                        <Icon size={18} strokeWidth={1.8} />
                        {link.label}
                      </button>
                    );
                  })}
                  {/* My Site sits with the primary links, same place as on desktop. */}
                  <button
                    type="button"
                    onClick={() => goTo('/my-site')}
                    className="flex items-center w-full text-left transition-colors"
                    style={{
                      gap: 14,
                      padding: '14px 12px',
                      borderRadius: 'var(--hj-r-sm)',
                      background: location.pathname === '/my-site' ? 'var(--hj-surface-2)' : 'transparent',
                      border: 0,
                      color: location.pathname === '/my-site' ? 'var(--hj-ink)' : 'var(--hj-ink-2)',
                      fontSize: 15,
                      fontWeight: location.pathname === '/my-site' ? 600 : 500,
                      fontFamily: 'inherit',
                      cursor: 'pointer',
                      minHeight: 44,
                    }}
                  >
                    <Globe size={18} strokeWidth={1.8} />
                    My Site
                  </button>
                  {/* Marketplace before Tools: buying, selling and your library are
                      what people open the menu for; the tool list is long and lives under it. */}
                  <div
                    className="mono-label"
                    style={{ marginTop: 14, marginBottom: 4, paddingLeft: 12, color: 'var(--hj-ink-2)' }}
                  >
                    Marketplace
                  </div>
                  {[
                    { path: '/discover', label: 'Discover', icon: Compass },
                    { path: '/marketplace', label: 'Browse marketplace', icon: ShoppingBag },
                    { path: '/my-store', label: 'My store', icon: Store },
                    { path: '/my-library', label: 'My library', icon: Video },
                  ].map(({ path, label, icon: Icon }) => {
                    const active = location.pathname === path;
                    return (
                      <button
                        key={path}
                        type="button"
                        onClick={() => goTo(path)}
                        className="flex items-center w-full text-left transition-colors"
                        style={{
                          gap: 14,
                          padding: '12px',
                          borderRadius: 'var(--hj-r-sm)',
                          background: active ? 'var(--hj-surface-2)' : 'transparent',
                          border: 0,
                          color: active ? 'var(--hj-ink)' : 'var(--hj-ink-2)',
                          fontSize: 14,
                          fontWeight: active ? 600 : 500,
                          fontFamily: 'inherit',
                          cursor: 'pointer',
                          minHeight: 44,
                        }}
                      >
                        <Icon size={16} strokeWidth={1.8} />
                        {label}
                      </button>
                    );
                  })}
                  {/* Tools — flattened to a single section header + items */}
                  <div
                    className="mono-label"
                    style={{ marginTop: 14, marginBottom: 4, paddingLeft: 12, color: 'var(--hj-ink-2)' }}
                  >
                    Tools
                  </div>
                  {allTools.map(({ path, label, icon: Icon }) => {
                    const active = location.pathname === path;
                    return (
                      <button
                        key={path}
                        type="button"
                        onClick={() => goTo(path)}
                        className="flex items-center w-full text-left transition-colors"
                        style={{
                          gap: 14,
                          padding: '12px',
                          borderRadius: 'var(--hj-r-sm)',
                          background: active ? 'var(--hj-surface-2)' : 'transparent',
                          border: 0,
                          color: active ? 'var(--hj-ink)' : 'var(--hj-ink-2)',
                          fontSize: 14,
                          fontWeight: active ? 600 : 500,
                          fontFamily: 'inherit',
                          cursor: 'pointer',
                          minHeight: 44,
                        }}
                      >
                        <Icon size={16} strokeWidth={1.8} />
                        {label}
                      </button>
                    );
                  })}
                </nav>

              </div>

              {/* Pinned footer: account, then the credits chip last. Credits used to
                  sit above the navigation and pushed the links off-screen. */}
              {isAuthenticated && (
                <div
                  className="flex-shrink-0"
                  style={{
                    borderTop: '1px solid var(--hj-hairline-soft)',
                    background: 'var(--hj-surface-1)',
                    padding: '10px 16px calc(14px + env(safe-area-inset-bottom))',
                  }}
                >
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => goTo('/account')}
                      className="flex items-center flex-1 text-left"
                      style={{
                        gap: 10,
                        padding: '10px 8px',
                        background: 'transparent',
                        border: 0,
                        color: 'var(--hj-ink)',
                        fontSize: 14,
                        fontWeight: 500,
                        fontFamily: 'inherit',
                        cursor: 'pointer',
                        minHeight: 44,
                      }}
                    >
                      <Settings size={16} strokeWidth={1.8} />
                      Manage Account
                      {rewardDot}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setMobileNavOpen(false);
                        logout();
                      }}
                      className="flex items-center flex-shrink-0"
                      style={{
                        gap: 8,
                        padding: '10px 8px',
                        background: 'transparent',
                        border: 0,
                        color: 'hsl(var(--destructive))',
                        fontSize: 14,
                        fontFamily: 'inherit',
                        cursor: 'pointer',
                        minHeight: 44,
                      }}
                    >
                      <LogOut size={16} strokeWidth={1.8} />
                      Log out
                    </button>
                  </div>
                  {creditsAuth() && (
                    <button
                      type="button"
                      onClick={() => {
                        setMobileNavOpen(false);
                        setShowPurchaseModal(true);
                      }}
                      className="flex items-center justify-between w-full transition-colors"
                      style={{
                        marginTop: 8,
                        padding: '10px 14px',
                        borderRadius: 'var(--hj-r-md)',
                        background: 'var(--hj-blue-tint)',
                        border: '1px solid var(--hj-blue)',
                        color: 'var(--hj-ink)',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        minHeight: 44,
                      }}
                    >
                      <span className="flex items-center gap-2.5">
                        <Coins size={17} strokeWidth={1.8} style={{ color: 'var(--hj-yellow-ink)' }} />
                        <span className="tabular" style={{ fontSize: 15, fontWeight: 700, color: 'var(--hj-ink)' }}>
                          {creditDisplay}
                        </span>
                        <span className="mono-label" style={{ fontSize: 10 }}>video tokens</span>
                      </span>
                      <span
                        className="inline-flex items-center gap-1"
                        style={{ color: 'var(--hj-yellow-ink)', fontSize: 13, fontWeight: 600 }}
                      >
                        <Plus size={14} strokeWidth={2} /> Buy more
                      </span>
                    </button>
                  )}
                </div>
              )}
            </SheetContent>
          </Sheet>

          {/* Wordmark center — sized down for sm. Same invert filter as the
              desktop variant so it renders cream on the dark header. */}
          <Link to="/" className="flex items-center gap-1.5">
            <img
              src="/brand/wordmark-heyjoe.png"
              alt="Hey Joe Plus"
              className="dark:[filter:invert(96%)_sepia(8%)_saturate(280%)_hue-rotate(345deg)_brightness(102%)]"
              style={{
                height: 28,
                width: 'auto',
                display: 'block',
              }}
            />
            {partnerBrand && <PartnerMark brand={partnerBrand} height={17} />}
            <span
              className="font-hj-display"
              style={{ fontSize: 16, color: 'var(--hj-ink)', marginLeft: 4 }}
            >
              Plus
            </span>
          </Link>

          {/* Right slot: avatar (or Sign in if logged out) — uniform across screens */}
          {isAuthenticated ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center justify-center"
                  style={{
                    width: 44,
                    height: 44,
                    margin: '0 -10px 0 0',
                    background: 'transparent',
                    border: 0,
                    cursor: 'pointer',
                  }}
                  aria-label="Account menu"
                >
                  <ProfileAvatar name={userName} size={32} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {userName && (
                  <DropdownMenuLabel className="font-normal">
                    <div className="text-xs text-muted-foreground">Signed in as</div>
                    <div className="text-sm font-medium">{userName}</div>
                  </DropdownMenuLabel>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setShowPurchaseModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Buy video tokens
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/account')}>
                  <Settings className="w-4 h-4 mr-2" />
                  Manage Account
                  {rewardDot}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={openCasting}>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Hey Joe Casting
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/marketplace')}>
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  Marketplace
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/calendar')}>
                  <CalendarDays className="w-4 h-4 mr-2" />
                  Featured event &amp; class calendar
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/my-store')}>
                  <Store className="w-4 h-4 mr-2" />
                  My Store
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/my-purchases')}>
                  <Package className="w-4 h-4 mr-2" />
                  My Purchases
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/my-subscriptions')}>
                  <Receipt className="w-4 h-4 mr-2" />
                  My Subscriptions
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/my-site')}>
                  <Globe className="w-4 h-4 mr-2" />
                  My Site
                </DropdownMenuItem>
                <DropdownMenuItem onClick={openCommunity}>
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Community
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/readers/requests')}>
                  <CalendarDays className="w-4 h-4 mr-2" />
                  Reader sessions
                </DropdownMenuItem>
                {isReader && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/readers/profile')}>
                      <BookOpen className="w-4 h-4 mr-2" />
                      My Reader Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/reader/payouts')}>
                      <Wallet className="w-4 h-4 mr-2" />
                      Reader Payouts
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-destructive focus:text-destructive">
                  <LogOut className="w-4 h-4 mr-2" />
                  Log Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button
              type="button"
              onClick={() => setShowLoginModal(true)}
              className="inline-flex items-center"
              style={{
                padding: '10px 14px',
                borderRadius: 999,
                border: '1px solid var(--hj-hairline)',
                background: 'transparent',
                color: 'var(--hj-ink)',
                fontSize: 13,
                fontFamily: 'inherit',
                cursor: 'pointer',
                minHeight: 44,
              }}
            >
              Sign in
            </button>
          )}
        </div>
      </header>

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={() => setShowLoginModal(false)}
      />
      <CreditPurchaseModal open={showPurchaseModal} onOpenChange={setShowPurchaseModal} />
    </>
  );
};

// Internal: nav link in the desktop bar.
const NavButton: React.FC<{
  label: string;
  active?: boolean;
  external?: boolean;
  onClick: () => void;
}> = ({ label, active, external, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center transition-colors hover:[color:var(--hj-ink-2)]"
      style={{
        gap: 8,
        padding: '8px 12px',
        borderRadius: 'var(--hj-r-sm)',
        background: 'transparent',
        border: 0,
        color: active ? 'var(--hj-ink)' : 'var(--hj-ink-2)',
        fontSize: 14,
        fontWeight: active ? 600 : 500,
        fontFamily: 'inherit',
        cursor: 'pointer',
      }}
    >
      <span>{label}</span>
      {external && (
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true" style={{ marginLeft: -2, opacity: 0.7 }}>
          <path d="M3.5 2.5h4v4M7 3L2.5 7.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </button>
  );
};
